import React from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

const RELATIONS = ['neighbor', 'family', 'friend', 'main_helper', 'other'];

type Props = {
  value: Array,
  onChange: Function,
  beneficiaries: Array,
};

const CompanionBeneficiariesField = ({ value, onChange, beneficiaries }: Props) => {
  const { t } = useTranslation();
  const available = beneficiaries || [];
  const links = value || [];

  const selectedBeneficiaries = links
    .map(link => available.find(b => b.id === link.beneficiary_helper_id))
    .filter(Boolean);

  const handleAddBeneficiaries = (event, newSelection) => {
    const newLinks = newSelection.map(beneficiary => {
      const existing = links.find(link => link.beneficiary_helper_id === beneficiary.id);
      return existing || { beneficiary_helper_id: beneficiary.id, relation: 'other' };
    });
    onChange(newLinks);
  };

  const handleRelationChange = (beneficiaryId, relation) => {
    onChange(
      links.map(link =>
        link.beneficiary_helper_id === beneficiaryId ? { ...link, relation } : link,
      ),
    );
  };

  const handleRemove = beneficiaryId => {
    onChange(links.filter(link => link.beneficiary_helper_id !== beneficiaryId));
  };

  return (
    <Grid container spacing={1} style={{ marginBottom: 16 }}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          {t('APP.ORGANISATION.SETTINGS.MEMBERS.INVITE.COMPANION.BENEFICIARIES_LABEL')}
        </Typography>
        <Autocomplete
          multiple
          options={available.filter(b => !links.some(link => link.beneficiary_helper_id === b.id))}
          getOptionLabel={b => `${b.first_name} ${b.last_name}`}
          value={selectedBeneficiaries}
          onChange={handleAddBeneficiaries}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={t(
                'APP.ORGANISATION.SETTINGS.MEMBERS.INVITE.COMPANION.SELECT_BENEFICIARIES',
              )}
            />
          )}
        />
      </Grid>
      {links.map(link => {
        const beneficiary = available.find(b => b.id === link.beneficiary_helper_id);
        if (!beneficiary) return null;
        return (
          <Grid
            item
            xs={12}
            container
            key={link.beneficiary_helper_id}
            alignItems="center"
            spacing={1}
          >
            <Grid item xs={5}>
              <Typography>{`${beneficiary.first_name} ${beneficiary.last_name}`}</Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={link.relation}
                  onChange={e => handleRelationChange(link.beneficiary_helper_id, e.target.value)}
                >
                  {RELATIONS.map(relation => (
                    <MenuItem key={relation} value={relation}>
                      {t(`APP.ROLE.VALIDATED.${relation.toUpperCase()}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => handleRemove(link.beneficiary_helper_id)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CompanionBeneficiariesField;
